import {
  PRINT_SCENES,
  isFieldVisible,
  isTopicAnnexQtyGirdVisible,
  isTopicAnnexNamesVisible,
  isTopicAnnexRowVisible,
  truncateText,
  truncatePrintProductName,
  formatChannelLabel,
} from "@/unit/printTemplateStorage.js";

function parseMm(val, fallback = 80) {
  if (val == null || val === "") {
    return fallback;
  }
  const num = parseFloat(String(val).replace("mm", ""));
  return Number.isFinite(num) ? num : fallback;
}

function parsePt(val, fallback = 11) {
  if (typeof val === "number") {
    return val;
  }
  if (!val) {
    return fallback;
  }
  const num = parseFloat(String(val).replace("pt", ""));
  return Number.isFinite(num) ? num : fallback;
}

function fontLineHmm(fontPt, padMm = 0.4) {
  return fontPt * 0.3527 + padMm;
}

function fitGirdFontSize(text, widthMm, heightMm, preferredPt) {
  const preferred = parsePt(preferredPt, 50);
  const len = Math.max(String(text || "").length, 1);
  const widthPerCharAt50 = 12;
  const maxByWidth = Math.floor(preferred * (widthMm / (len * widthPerCharAt50)));
  const maxByHeight = Math.floor((heightMm - 1) / 0.3527);
  return Math.max(22, Math.min(preferred, maxByWidth, maxByHeight));
}

function fitHeaderFontSize(text, widthMm, heightMm, preferredPt, minPt = 10) {
  const preferred = parsePt(preferredPt, 14);
  const len = Math.max(String(text || "").length, 1);
  const widthPerChar = preferred * 0.22;
  const maxByWidth = Math.floor(preferred * (widthMm / (len * widthPerChar)));
  const maxByHeight = Math.floor((heightMm - 0.5) / 0.3527);
  return Math.max(minPt, Math.min(preferred, maxByWidth, maxByHeight));
}

function estimateTextLines(text, widthMm, fontPt, bold = false) {
  return splitTextToLines(text, widthMm, fontPt, 4, bold).length;
}

function calcWrappedTextHeight(text, widthMm, fontPt, lineHmm, maxLines = 4, bold = false) {
  const lines = splitTextToLines(text, widthMm, fontPt, maxLines, bold);
  return lineHmm * lines.length + Math.max(0, lines.length - 1) * 0.2;
}

function charWidthMm(ch, fontPt, bold = false) {
  const base = fontPt * 0.3527;
  if (/[\x00-\xff]/.test(ch)) {
    return base * (bold ? 0.48 : 0.36);
  }
  return base * (bold ? 0.72 : 0.56);
}

function estimateTextWidthMm(text, fontPt, bold = false, padMm = 2) {
  const contentW = String(text || "")
    .split("")
    .reduce((sum, ch) => sum + charWidthMm(ch, fontPt, bold), 0);
  return contentW * 1.45 + padMm;
}

function splitTextToLines(text, widthMm, fontPt, maxLines = 4, bold = false) {
  const str = String(text || "");
  if (!str) {
    return [];
  }
  const lines = [];
  let current = "";
  let currentW = 0;
  for (const ch of str) {
    const w = charWidthMm(ch, fontPt, bold);
    if (current && currentW + w > widthMm && lines.length < maxLines - 1) {
      lines.push(current);
      current = ch;
      currentW = w;
    } else {
      current += ch;
      currentW += w;
    }
  }
  if (current) {
    lines.push(current);
  }
  return lines.slice(0, maxLines);
}

function addMultilineText(LODOP, startY, leftMm, widthMm, lineHmm, gapMm, text, style = {}, maxLines = 4) {
  const fontPt = style.fontSize || 11;
  const lines = splitTextToLines(text, widthMm, fontPt, maxLines, style.bold);
  let y = startY;
  lines.forEach((line, index) => {
    addText(LODOP, mm(y), mm(leftMm), mm(widthMm), mm(lineHmm), line, style);
    y += lineHmm + (index < lines.length - 1 ? gapMm : 0);
  });
  return y + gapMm;
}

function formatAnnexNameText(names) {
  if (names == null || names === "") {
    return "";
  }
  if (Array.isArray(names)) {
    return names.map(String).map((s) => s.trim()).filter(Boolean).join(",");
  }
  return String(names).trim();
}

const TOPIC_ANNEX_MAX_LINES = 4;

/** 附件区可用至右栏起点，吃掉 midX~rightX 之间的空白 */
function calcTopicLeftAnnexWidth(margin, rightX) {
  return Math.max(0, rightX - margin - 0.15);
}

function addAnnexLines(
  LODOP,
  startY,
  leftMm,
  lineHmm,
  gapMm,
  annexNum,
  annexGird,
  annexNames,
  style,
  annexColW,
  tpl,
) {
  const showQtyGird = isTopicAnnexQtyGirdVisible(tpl);
  const showNames = isTopicAnnexNamesVisible(tpl);
  if (!showQtyGird && !showNames) {
    return startY;
  }
  const preferredFont = style.fontSize || 11;
  let text = "";
  if (showQtyGird) {
    text = `附件:${annexNum != null ? annexNum : ""};${annexGird || ""};`;
  }
  const annexText = showNames ? formatAnnexNameText(annexNames) : "";
  if (annexText) {
    text = showQtyGird ? `${text}${annexText}` : annexText;
  }
  if (!text.trim()) {
    return startY;
  }
  const boxH = lineHmm * TOPIC_ANNEX_MAX_LINES + gapMm * (TOPIC_ANNEX_MAX_LINES - 1);
  const annexFont = fitHeaderFontSize(text, annexColW, boxH, preferredFont, 9);
  const annexStyle = {
    fontSize: annexFont,
    fontName: "宋体",
    bold: true,
  };

  addText(LODOP, mm(startY), mm(leftMm), mm(annexColW), mm(boxH), text, {
    ...annexStyle,
    wordWrap: true,
    lineSpacing: -2,
  });
  return startY + boxH + gapMm;
}

function resolveStoreAnnexNames(productAnnex) {
  if (productAnnex == null || productAnnex === "") {
    return [];
  }
  if (Array.isArray(productAnnex)) {
    return productAnnex.map(String).map((s) => s.trim()).filter(Boolean);
  }
  const parts = String(productAnnex)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 1 && /^\d+$/.test(parts[0])) {
    return [];
  }
  return parts;
}

function resolveStoreAnnexCount(productAnnex, annexNames) {
  if (annexNames.length) {
    return annexNames.length;
  }
  const num = Number(productAnnex);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

function calcAutoTopicBarcodeWidth(codeLen, pageW, isOnline) {
  if (isOnline) {
    if (codeLen <= 13) {
      return pageW * 0.82;
    }
    return pageW * 0.88;
  }
  if (codeLen <= 8) {
    return pageW * 0.62;
  }
  if (codeLen <= 12) {
    return pageW * 0.72;
  }
  return pageW * 0.78;
}

function resolveTopicBarcode(tpl, pageW, pageH, code, scene) {
  const bc = (tpl && tpl.topicBarcode) || {};
  const isOnline = scene === PRINT_SCENES.ONLINE_TOPIC;
  const codeLen = String(code || "").length;
  const autoWidth = calcAutoTopicBarcodeWidth(codeLen, pageW, isOnline);
  const autoHeight = isOnline ? 20 : 18;
  const autoTop = isOnline ? 60 : 60;
  const autoTextSize = isOnline ? 12 : 14;

  const margin = 2;
  let width = bc.width != null && bc.width !== ""
    ? Math.max(parseMm(bc.width, autoWidth), autoWidth)
    : autoWidth;
  width = Math.min(width, Math.max(pageW - margin * 2, autoWidth * 0.5));
  const height = bc.height != null && bc.height !== ""
    ? parseMm(bc.height, autoHeight)
    : autoHeight;
  const top = bc.top != null && bc.top !== ""
    ? parseMm(bc.top, autoTop)
    : autoTop;
  const useCustomLeft = bc.left != null && String(bc.left).trim() !== "";
  let horient = 2;
  if (bc.horient != null && bc.horient !== "") {
    horient = Number(bc.horient);
  }
  if (!Number.isFinite(horient)) {
    horient = 2;
  }
  let left;
  if (useCustomLeft) {
    left = parseMm(bc.left, margin);
  } else if (horient === 2) {
    left = Math.max(margin, (pageW - width) / 2);
    horient = 0;
  } else {
    left = Math.max(margin, (pageW - width) / 2);
  }
  let textFontSize = autoTextSize;
  if (bc.textFontSize != null && bc.textFontSize !== "") {
    textFontSize = typeof bc.textFontSize === "number"
      ? bc.textFontSize
      : parsePt(bc.textFontSize, autoTextSize);
  } else if (tpl.barcodeJs && tpl.barcodeJs.fontSize != null) {
    textFontSize = tpl.barcodeJs.fontSize;
  }
  return { width, height, top, left, textFontSize, horient };
}

function addTopicBarcode(LODOP, tpl, code, pageW, pageH, scene) {
  const bc = resolveTopicBarcode(tpl, pageW, pageH, code, scene);
  LODOP.ADD_PRINT_BARCODE(
    mm(bc.top),
    mm(bc.left),
    mm(bc.width),
    mm(bc.height),
    "128Auto",
    code,
  );
  LODOP.SET_PRINT_STYLEA(0, "ShowBarText", isFieldVisible(tpl, "barcodeText") ? 1 : 0);
  LODOP.SET_PRINT_STYLEA(0, "Horient", bc.horient != null ? bc.horient : 2);
  if (isFieldVisible(tpl, "barcodeText")) {
    LODOP.SET_PRINT_STYLEA(0, "FontSize", bc.textFontSize);
    LODOP.SET_PRINT_STYLEA(0, "Alignment", 2);
  }
}

function calcAutoWashBarcodeWidth(codeLen, pageW, isOnline, isAnnex) {
  if (isOnline) {
    if (codeLen <= 13) {
      return pageW * 0.38;
    }
    return pageW * 0.42;
  }
  if (codeLen <= 8) {
    return pageW * 0.32;
  }
  if (codeLen <= 12) {
    return pageW * 0.36;
  }
  return pageW * 0.4;
}

function resolveWashBarcode(tpl, pageW, pageH, code, scene, isAnnex) {
  const configured = isAnnex ? tpl.annexWashBarcode : tpl.washBarcode;
  const legacy = isAnnex ? tpl.annexBarcode : tpl.barcode;
  const bc = configured || {};
  const isOnline = scene === PRINT_SCENES.ONLINE_WASH;
  const codeLen = String(code || "").length;
  const autoWidth = calcAutoWashBarcodeWidth(codeLen, pageW, isOnline, isAnnex);
  const autoHeight = 10;
  const autoTop = 5;
  const autoLeft = isAnnex ? 2 : 3;

  if (!configured && legacy) {
    const legacyWidth = parseFloat(String(legacy.width || "").replace("px", "")) || autoWidth;
    const legacyHeight = typeof legacy.height === "number" ? legacy.height : autoHeight;
    const legacyTop = typeof legacy.top === "number" ? legacy.top * 0.26 : autoTop;
    return {
      top: legacyTop,
      left: parseMm(legacy.left, autoLeft),
      width: legacyWidth > 20 ? legacyWidth * 0.26 : parseMm(legacy.width, autoWidth),
      height: legacyHeight > 15 ? legacyHeight * 0.26 : parseMm(legacy.height, autoHeight),
    };
  }

  return {
    top: bc.top != null && bc.top !== "" ? parseMm(bc.top, autoTop) : autoTop,
    left: bc.left != null && bc.left !== "" ? parseMm(bc.left, autoLeft) : autoLeft,
    width: bc.width != null && bc.width !== ""
      ? Math.max(parseMm(bc.width, autoWidth), autoWidth * 0.9)
      : autoWidth,
    height: bc.height != null && bc.height !== "" ? parseMm(bc.height, autoHeight) : autoHeight,
  };
}

function addWashBarcode(LODOP, tpl, code, pageW, pageH, scene, isAnnex) {
  const bc = resolveWashBarcode(tpl, pageW, pageH, code, scene, isAnnex);
  LODOP.ADD_PRINT_BARCODE(
    mm(bc.top),
    mm(bc.left),
    mm(bc.width),
    mm(bc.height),
    "128Auto",
    code,
  );
  LODOP.SET_PRINT_STYLEA(0, "ShowBarText", 0);
  LODOP.SET_PRINT_STYLEA(0, "BarcodeWidthFix", 1);
}

function mm(v) {
  return `${v}mm`;
}

/** 总检码正文左右分栏：左窄右宽，便于显示完整日期时间 */
const TOPIC_BODY_LEFT_SPLIT = 0.4;
const TOPIC_BODY_RIGHT_START = 0.42;

function addTopicCompleteMark(LODOP, tpl, pageW, margin, bodyTop, bodyBottom, show) {
  if (!isFieldVisible(tpl, "completeMark") || !show) {
    return;
  }
  const qiZoneW = pageW * 0.18;
  const qiLeft = pageW - margin - qiZoneW;
  addText(
    LODOP,
    mm(bodyTop + 0.6),
    mm(qiLeft),
    mm(qiZoneW),
    mm(bodyBottom - bodyTop - 1),
    "齐",
    {
      fontSize: parsePt(tpl.completeMarkFontSize, 28),
      bold: true,
      alignment: 2,
      fontName: "楷体",
    },
  );
}

function addStoreMetaLine(LODOP, topMm, leftMm, widthMm, heightMm, text, style = {}) {
  if (text == null || text === "") {
    return;
  }
  addText(LODOP, mm(topMm), mm(leftMm), mm(widthMm), mm(heightMm), text, {
    fontName: "宋体",
    ...style,
    wordWrap: false,
    alignment: 3,
  });
}

function addStoreMetaTextIf(LODOP, tpl, fieldKey, topMm, leftMm, widthMm, heightMm, text, style = {}) {
  if (!isFieldVisible(tpl, fieldKey) || text == null || text === "") {
    return;
  }
  addStoreMetaLine(LODOP, topMm, leftMm, widthMm, heightMm, text, style);
}

function addTopicRightFields(LODOP, tpl, opts) {
  const {
    bodyTop,
    rightX,
    rightW,
    lineH,
    bodyFont,
    phoneKey,
    phoneLabel,
    phoneValue,
    fields,
  } = opts;
  const valueLineH = Math.max(lineH, bodyFont * 0.36);

  if (isFieldVisible(tpl, phoneKey)) {
    addText(LODOP, mm(bodyTop + 1), mm(rightX), mm(rightW), mm(lineH), phoneLabel, {
      fontSize: bodyFont,
      fontName: "宋体",
    });
    addText(LODOP, mm(bodyTop + 1 + lineH), mm(rightX), mm(rightW), mm(valueLineH), phoneValue, {
      fontSize: bodyFont,
      fontName: "宋体",
    });
  }

  let rightY = bodyTop + lineH * 2.4;
  fields.forEach(([key, label, value]) => {
    if (!isFieldVisible(tpl, key)) {
      return;
    }
    addText(LODOP, mm(rightY), mm(rightX), mm(rightW), mm(lineH), label, {
      fontSize: bodyFont,
      fontName: "宋体",
    });
    rightY += lineH;
    addText(LODOP, mm(rightY), mm(rightX), mm(rightW), mm(valueLineH), value, {
      fontSize: bodyFont,
      fontName: "宋体",
      wordWrap: false,
    });
    rightY += valueLineH;
  });
}

function applyTextStyle(LODOP, style = {}) {
  if (style.fontSize != null) {
    LODOP.SET_PRINT_STYLEA(0, "FontSize", style.fontSize);
  }
  if (style.alignment != null) {
    LODOP.SET_PRINT_STYLEA(0, "Alignment", style.alignment);
  }
  let fontName = style.fontName || "宋体";
  if (style.bold && fontName === "宋体") {
    fontName = "黑体";
  }
  LODOP.SET_PRINT_STYLEA(0, "FontName", fontName);
  if (style.bold) {
    LODOP.SET_PRINT_STYLEA(0, "Bold", 1);
  }
  if (style.wordWrap != null) {
    LODOP.SET_PRINT_STYLEA(0, "WordWrap", style.wordWrap ? 1 : 0);
  }
  if (style.lineSpacing != null) {
    LODOP.SET_PRINT_STYLEA(0, "LineSpacing", style.lineSpacing);
  }
}

function addText(LODOP, top, left, width, height, text, style = {}) {
  if (text == null || text === "") {
    return;
  }
  LODOP.ADD_PRINT_TEXT(top, left, width, height, String(text));
  applyTextStyle(LODOP, style);
}

function addTextIf(LODOP, tpl, key, top, left, width, height, text, style = {}) {
  if (!isFieldVisible(tpl, key)) {
    return;
  }
  addText(LODOP, top, left, width, height, text, style);
}

function resolveWashCodeTextBox(tpl) {
  const box = (tpl && tpl.washCodeText) || {};
  return {
    top: box.top != null && box.top !== "" ? box.top : "16.5mm",
    left: box.left != null && box.left !== "" ? box.left : "2mm",
    width: box.width != null && box.width !== "" ? box.width : "38mm",
    height: box.height != null && box.height !== "" ? box.height : "3mm",
  };
}

function addLine(LODOP, y1, x1, y2, x2) {
  LODOP.ADD_PRINT_LINE(mm(y1), mm(x1), mm(y2), mm(x2), 0, 1);
}

function initPage(LODOP, tpl, printName) {
  LODOP.SET_PRINT_PAGESIZE(1, tpl.pageWidth, tpl.pageHeight);
  LODOP.SET_PRINT_MODE("POS_BASEON_PAPER", true);
  LODOP.SET_PRINTER_INDEX(printName != null ? printName : -1);
  LODOP.SET_PRINT_COPIES(1);
}

function refundText(item) {
  const parts = [];
  if (item.refundType) {
    parts.push("【退洗】");
  }
  if (item.backStatus) {
    parts.push("【返洗】");
  }
  return parts.join("");
}

function resolveOnlineTopicHeaderMeta(pageH, tpl) {
  const plantPt = parsePt(tpl.plantFontSize, 13);
  const channelPt = parsePt(tpl.channelFontSize, 16);
  const compact = pageH <= 45;
  const plantH = compact
    ? fontLineHmm(plantPt, 0.05)
    : Math.max(3.5, fontLineHmm(plantPt, 0.15));
  const channelH = compact
    ? fontLineHmm(channelPt, 0.08)
    : Math.max(4.5, fontLineHmm(channelPt, 0.2));
  const metaGap = compact ? 0.15 : 0.55;
  return { plantH, channelH, metaGap };
}

function buildOnlineTopicNative(LODOP, tpl, item, printName) {
  initPage(LODOP, tpl, printName);
  const pageW = parseMm(tpl.pageWidth, 80);
  const pageH = parseMm(tpl.pageHeight, 80);
  const margin = 2;
  const headerH = pageH * 0.22;
  const bodyTop = headerH + 1.2;
  const bodyBottom = pageH * 0.72;
  const midX = pageW * TOPIC_BODY_LEFT_SPLIT;
  const rightX = pageW * TOPIC_BODY_RIGHT_START;
  const lineH = Math.max(3.5, pageH * 0.052);
  const leftLineH = lineH * 1.12;
  const leftGap = 0.35;

  const channel = formatChannelLabel(
    item.sourceType,
    tpl.channelWrap,
    tpl.truncate.channel,
  );
  const customer = truncateText(item.name, tpl.truncate.name, "");
  const product = truncatePrintProductName(
    item.productName,
    tpl,
    PRINT_SCENES.ONLINE_TOPIC,
  );
  const metaLeft = pageW * 0.62;
  const metaWidth = pageW - margin - metaLeft;
  const { plantH, channelH, metaGap } = resolveOnlineTopicHeaderMeta(pageH, tpl);
  const metaBlockH = plantH + metaGap + channelH;
  const metaTop = Math.max(0.8, (headerH - metaBlockH) / 2 + 0.6);
  const girdW = metaLeft - margin - 0.5;
  const girdH = headerH + 1.5;
  const girdPt = fitGirdFontSize(item.girdNumber, girdW, girdH, tpl.girdFontSize);

  addTextIf(
    LODOP,
    tpl,
    "plant",
    mm(metaTop),
    mm(metaLeft),
    mm(metaWidth),
    mm(plantH),
    truncateText(item.plant, tpl.truncate && tpl.truncate.plant, item.plant || ""),
    {
      fontSize: parsePt(tpl.plantFontSize, 13),
      alignment: 2,
      fontName: "宋体",
      wordWrap: false,
      lineSpacing: pageH <= 45 ? -3 : 0,
    },
  );
  addTextIf(
    LODOP,
    tpl,
    "channel",
    mm(metaTop + plantH + metaGap),
    mm(metaLeft),
    mm(metaWidth),
    mm(channelH),
    channel,
    {
      fontSize: parsePt(tpl.channelFontSize, 16),
      bold: true,
      alignment: 2,
      fontName: "宋体",
      lineSpacing: pageH <= 45 ? -3 : 0,
    },
  );

  addLine(LODOP, bodyTop - 0.8, margin, bodyTop - 0.8, pageW - margin);
  addLine(LODOP, bodyTop, midX, bodyBottom, midX);

  addTextIf(
    LODOP,
    tpl,
    "girdNumber",
    mm(0.5),
    mm(margin),
    mm(girdW),
    mm(girdH),
    item.girdNumber,
    {
      fontSize: girdPt,
      bold: true,
      alignment: 1,
      fontName: "楷体",
      wordWrap: false,
    },
  );

  let leftY = bodyTop + 1;
  const leftW = midX - margin - 0.3;
  const leftAnnexW = calcTopicLeftAnnexWidth(margin, rightX);
  const rightW = pageW - rightX - margin;
  const bodyFont = parsePt(tpl.defaultFontSize, 11);

  const leftFields = [
    ["refundTag", refundText(item)],
    ["customer", customer],
    ["product", product],
    [
      "quantity",
      item.sum ? `共${item.sum}件  ${item.orderSort}/${item.sum}` : "",
    ],
    [
      "annex",
      item.annexNum != null ? String(item.annexNum) : "",
    ],
  ];
  leftFields.forEach(([key, text]) => {
    if (key === "annex") {
      if (!isTopicAnnexRowVisible(tpl)) {
        return;
      }
      if (
        isTopicAnnexQtyGirdVisible(tpl) &&
        item.annexNum == null &&
        !isTopicAnnexNamesVisible(tpl)
      ) {
        return;
      }
    } else if (!isFieldVisible(tpl, key) || !text) {
      return;
    }
    const style = {
      fontSize: bodyFont,
      fontName: "宋体",
      bold: key === "quantity" || key === "annex",
    };
    if (key === "quantity") {
      addText(LODOP, mm(leftY), mm(margin), mm(leftW), mm(leftLineH), text, {
        fontSize: bodyFont,
        fontName: "黑体",
        bold: true,
      });
      leftY += leftLineH + leftGap;
      return;
    }
    if (key === "annex") {
      leftY = addAnnexLines(
        LODOP,
        leftY,
        margin,
        leftLineH,
        leftGap * 0.35,
        item.annexNum,
        item.annexGird,
        item.annex,
        style,
        leftAnnexW,
        tpl,
      );
      return;
    }
    addText(LODOP, mm(leftY), mm(margin), mm(leftW), mm(leftLineH), text, style);
    leftY += leftLineH + leftGap;
  });

  const factoryTimeLabel =
    (tpl.topicRightLabels && tpl.topicRightLabels.factoryTime) || "入厂时间";
  addTopicRightFields(LODOP, tpl, {
    bodyTop,
    rightX,
    rightW,
    lineH,
    bodyFont,
    phoneKey: "phone",
    phoneLabel: "电话号码",
    phoneValue: item.phone,
    fields: [
      ["orderSn", "订单编号", item.orderSn],
      ["factoryTime", factoryTimeLabel, item.factoryTime],
      ["inspectTime", "总检时间", item.time],
    ],
  });

  if (isFieldVisible(tpl, "completeMark") && item.isTxt) {
    addTopicCompleteMark(LODOP, tpl, pageW, margin, bodyTop, bodyBottom, true);
  }

  if (isFieldVisible(tpl, "barcode") && item.code) {
    addTopicBarcode(LODOP, tpl, item.code, pageW, pageH, PRINT_SCENES.ONLINE_TOPIC);
  }
}

function buildStoreTopicNative(LODOP, tpl, item, printName, grillObj = {}, isText = false) {
  initPage(LODOP, tpl, printName);
  const pageW = parseMm(tpl.pageWidth, 80);
  const pageH = parseMm(tpl.pageHeight, 80);
  const margin = 2;
  const midX = pageW * TOPIC_BODY_LEFT_SPLIT;
  const rightX = pageW * TOPIC_BODY_RIGHT_START;
  const lineH = Math.max(3.5, pageH * 0.052);
  const leftLineH = lineH * 1.12;
  const leftGap = 0.35;

  const stage = truncateText(item.stageName, tpl.truncate.stage, "");
  const girdNumber = grillObj.autoGrillage || "";
  const channel = formatChannelLabel(
    item.channelName,
    tpl.channelWrap,
    tpl.truncate.channel,
  );
  const driver = truncateText(item.tcDriverName, tpl.truncate.driver, "");
  const customer = truncateText(
    item.senderName,
    tpl.truncate && tpl.truncate.name > 0 ? tpl.truncate.name : 8,
    item.senderName || "",
  );
  const product = truncatePrintProductName(
    item.productName,
    tpl,
    PRINT_SCENES.STORE_TOPIC,
  );
  const metaLeft = pageW * 0.62;
  const metaWidth = pageW - margin - metaLeft;
  const headerMetaLeft = midX;
  const headerMetaW = pageW - margin - headerMetaLeft;
  const girdW = metaLeft - margin - 0.5;
  const girdTop = 0.1;
  const girdPt = fitGirdFontSize(girdNumber, girdW, 12, tpl.girdFontSize);
  const girdLineH = fontLineHmm(girdPt);
  const stageFont = parsePt(tpl.stageFontSize, 14);
  const stageLineH = fontLineHmm(stageFont, 0.3);
  const showStage = isFieldVisible(tpl, "stageName") && stage;
  const stageTop = girdTop + girdLineH + (showStage ? 0.08 : 0);
  const headerLeftBottom = showStage ? stageTop + stageLineH : girdTop + girdLineH;
  const driverH = 3.5;
  const channelH = 6.5;
  const metaGap = 0.35;
  const channelPt = fitHeaderFontSize(channel, headerMetaW, channelH, tpl.channelFontSize, 10);
  const metaBlockH = driverH + metaGap + channelH;
  const metaTop = Math.max(0.35, (headerLeftBottom - metaBlockH) / 2);
  const bodyTop = Math.max(headerLeftBottom, metaTop + metaBlockH) + 0.45;
  const bodyBottom = pageH * 0.72;

  addLine(LODOP, bodyTop - 0.45, margin, bodyTop - 0.45, pageW - margin);
  addLine(LODOP, bodyTop, midX, bodyBottom, midX);

  addTextIf(
    LODOP,
    tpl,
    "girdNumber",
    mm(girdTop),
    mm(margin),
    mm(girdW),
    mm(girdLineH),
    girdNumber,
    {
      fontSize: girdPt,
      bold: true,
      alignment: 1,
      fontName: "楷体",
      wordWrap: false,
    },
  );
  addTextIf(
    LODOP,
    tpl,
    "stageName",
    mm(stageTop),
    mm(margin),
    mm(girdW),
    mm(stageLineH),
    stage,
    {
      fontSize: parsePt(tpl.stageFontSize, 14),
      bold: true,
      alignment: 1,
      fontName: "楷体",
      wordWrap: true,
    },
  );
  addStoreMetaTextIf(
    LODOP,
    tpl,
    "channel",
    metaTop,
    headerMetaLeft,
    headerMetaW,
    channelH,
    channel,
    {
      fontSize: channelPt,
      bold: true,
      fontName: "楷体",
    },
  );
  addStoreMetaTextIf(
    LODOP,
    tpl,
    "driver",
    metaTop + channelH + metaGap,
    headerMetaLeft,
    headerMetaW,
    driverH,
    driver,
    {
      fontSize: parsePt(tpl.driverFontSize, 10),
      bold: true,
      fontName: "宋体",
    },
  );

  let leftY = bodyTop + 1;
  const leftW = midX - margin - 0.3;
  const leftAnnexW = calcTopicLeftAnnexWidth(margin, rightX);
  const rightW = pageW - rightX - margin;
  const bodyFont = parsePt(tpl.defaultFontSize, 11);

  const leftFields = [
    ["refundTag", refundText(item)],
    ["customer", customer],
    ["product", product],
    [
      "quantity",
      item.totalQuantity ? `共${item.totalQuantity}件` : "",
    ],
    [
      "annex",
      "__store_annex__",
    ],
  ];
  leftFields.forEach(([key, text]) => {
    if (key === "annex") {
      if (!isTopicAnnexRowVisible(tpl)) {
        return;
      }
      const annexNames = resolveStoreAnnexNames(item.productAnnex);
      const annexCount = resolveStoreAnnexCount(item.productAnnex, annexNames);
      if (
        !annexCount &&
        !annexNames.length &&
        isTopicAnnexQtyGirdVisible(tpl)
      ) {
        return;
      }
      const annexStyle = {
        fontSize: bodyFont,
        fontName: "宋体",
        bold: true,
      };
      leftY = addAnnexLines(
        LODOP,
        leftY,
        margin,
        leftLineH,
        leftGap * 0.35,
        annexCount,
        grillObj.handGrillage,
        annexNames.join(","),
        annexStyle,
        leftAnnexW,
        tpl,
      );
      return;
    }
    if (!isFieldVisible(tpl, key)) {
      return;
    }
    if (!text) {
      return;
    }
    const style = {
      fontSize: bodyFont,
      fontName: "宋体",
      bold: key === "quantity" || key === "annex",
    };
    if (key === "quantity") {
      addText(LODOP, mm(leftY), mm(margin), mm(leftW), mm(leftLineH), text, {
        fontSize: bodyFont,
        fontName: "黑体",
        bold: true,
      });
      leftY += leftLineH + leftGap;
      return;
    }
    addText(LODOP, mm(leftY), mm(margin), mm(leftW), mm(leftLineH), text, style);
    leftY += leftLineH + leftGap;
  });

  addTopicRightFields(LODOP, tpl, {
    bodyTop,
    rightX,
    rightW,
    lineH,
    bodyFont,
    phoneKey: "phone",
    phoneLabel: "电话号码",
    phoneValue: item.senderPhone,
    fields: [
      ["orderSn", "订单编号", item.orderSn],
      ["sortingTime", "分检时间", item.sortingTime],
      ["inspectTime", "总检时间", item.giTime || item.totalSelectionTime],
    ],
  });

  if (isFieldVisible(tpl, "completeMark") && isText) {
    addTopicCompleteMark(LODOP, tpl, pageW, margin, bodyTop, bodyBottom, true);
  }

  if (isFieldVisible(tpl, "barcode") && item.washingCode) {
    addTopicBarcode(LODOP, tpl, item.washingCode, pageW, pageH, PRINT_SCENES.STORE_TOPIC);
  }
}

function addWashRightAnchoredText(LODOP, tpl, fieldKey, topMm, pageW, boxHeightMm, text, style = {}) {
  if (fieldKey && !isFieldVisible(tpl, fieldKey)) {
    return;
  }
  if (!text) {
    return;
  }
  const marginRight = 2;
  const boxW = pageW * 0.48;
  const left = pageW - marginRight - boxW;
  addText(LODOP, mm(topMm), mm(left), mm(boxW), mm(boxHeightMm), text, {
    alignment: 3,
    ...style,
  });
}

function buildWashNative(LODOP, tpl, data, options = {}) {
  const {
    code,
    printName,
    isAnnex = false,
    annexLabel = "",
    annexProduct = "",
    scene = PRINT_SCENES.ONLINE_WASH,
  } = options;

  initPage(LODOP, tpl, printName);
  const pageW = parseMm(tpl.pageWidth, 100);
  const bodyFont = parsePt(tpl.defaultFontSize, 9);
  const isStoreWash = scene === PRINT_SCENES.STORE_WASH;
  const channelFont = parsePt(
    tpl.channelFontSize || tpl.defaultFontSize,
    isStoreWash ? 11 : 13,
  );

  addWashBarcode(LODOP, tpl, code, pageW, parseMm(tpl.pageHeight, 20), scene, isAnnex);

  const title = data.title || "";
  const washCodeText = isAnnex ? `附 ${code}` : `主 ${code}`;
  const customer = data.customer || "";
  const phone = data.phone || "";
  const channel = data.channel || "";
  const gird = data.gird || "";
  const product = data.product || "";
  const flaw = data.flaw || "";
  const annex = data.annex || "";
  const refundTag = data.refundTag || "";

  addTextIf(LODOP, tpl, "title", "1mm", "2mm", "36mm", "4mm", title, {
    fontSize: bodyFont,
    bold: true,
    fontName: "宋体",
  });
  const washCodeBox = resolveWashCodeTextBox(tpl);
  addTextIf(
    LODOP,
    tpl,
    "washCode",
    washCodeBox.top,
    washCodeBox.left,
    washCodeBox.width,
    washCodeBox.height,
    washCodeText,
    {
      fontSize: bodyFont,
      bold: true,
      fontName: "宋体",
    },
  );

  const customerLine = [customer, phone].filter(Boolean).join("  ");
  addTextIf(LODOP, tpl, "customer", "1mm", "40mm", "38mm", "4mm", customerLine, {
    fontSize: bodyFont,
    fontName: "宋体",
  });
  if (isAnnex && isFieldVisible(tpl, "product") && annexProduct) {
    addText(LODOP, "5mm", "40mm", "58mm", "4mm", annexProduct, {
      fontSize: bodyFont,
      fontName: "宋体",
    });
  } else {
    addTextIf(LODOP, tpl, "product", "5mm", "40mm", "58mm", "4mm", product, {
      fontSize: bodyFont,
      fontName: "宋体",
    });
  }
  addTextIf(LODOP, tpl, "flaw", "9mm", "40mm", "52mm", "4mm", flaw ? `瑕疵:${flaw}` : "", {
    fontSize: bodyFont,
    fontName: "宋体",
  });
  addTextIf(LODOP, tpl, "annex", "13mm", "40mm", "52mm", "4mm", annex ? `附件:${annex}` : "", {
    fontSize: bodyFont,
    fontName: "宋体",
  });
  const girdText = `${gird} ${refundTag}`.trim();
  addWashRightAnchoredText(LODOP, tpl, "girdNumber", "14mm", pageW, "3.5mm", girdText, {
    fontSize: parsePt(tpl.girdFontSize, 13),
    bold: true,
    fontName: "楷体",
  });
  // 渠道来源浮层：统一位置，最后绘制以免挤压其它字段
  addWashRightAnchoredText(LODOP, tpl, "channel", "4.5mm", pageW, "4mm", channel, {
    fontSize: channelFont,
    bold: true,
    fontName: "楷体",
  });
}

export function mapOnlineWashPayload(item, tpl) {
  const title = `${truncateText(item.plant, tpl.truncate.title, "")}  ${item.time || ""}`.trim();
  const customer = truncateText(item.name, tpl.truncate.name, "无");
  const channel = formatChannelLabel(item.sourceType, tpl.channelWrap, tpl.truncate.channel);
  const gird = [
    item.girdNumber,
    item.annexNum != 0 ? item.annexGird : "",
  ]
    .filter(Boolean)
    .join("  ");
  const productName = truncatePrintProductName(
    item.productName,
    tpl,
    PRINT_SCENES.ONLINE_WASH,
  );
  const product = [
    productName,
    item.sum ? `共${item.sum}件` : "",
  ]
    .filter(Boolean)
    .join("  ");
  const flaw = truncateText(item.flaw, tpl.truncate.flaw, "");
  const annex = [
    item.annexNum,
    Array.isArray(item.annex) ? item.annex.join() : item.annex,
  ]
    .filter((v) => v != null && v !== "")
    .join(";");
  return { title, customer, phone: item.phone, channel, gird, product, flaw, annex };
}

export function resolveStoreWashGrillObj(item = {}, grillObj = {}) {
  const annexRaw = item.productAnnex;
  const annexList = annexRaw
    ? (Array.isArray(annexRaw) ? annexRaw : String(annexRaw).split(","))
      .map((s) => String(s).trim())
      .filter(Boolean)
    : [];
  const hasAnnex = annexList.length > 0 || Number(item.annexNum) > 0;
  const autoGrillage =
    grillObj.autoGrillage ||
    item.autoGrillage ||
    item.grillage ||
    item.girdNumber ||
    "";
  const handGrillage =
    grillObj.handGrillage ||
    item.handGrillage ||
    item.annexGrillage ||
    item.annexGird ||
    "";

  return {
    autoGrillage: String(autoGrillage || "").trim(),
    handGrillage: hasAnnex ? String(handGrillage || "").trim() : "",
  };
}

export function mapStoreWashPayload(item, tpl, grillObj = {}) {
  const title = `${truncateText(item.stageName, tpl.truncate.title, "")}  ${item.time || ""}`.trim();
  const customer = truncateText(item.senderName, tpl.truncate.name, "无");
  const channel = formatChannelLabel(item.channelName, tpl.channelWrap, tpl.truncate.channel);
  const resolvedGrill = resolveStoreWashGrillObj(item, grillObj);
  const gird = [
    resolvedGrill.autoGrillage,
    resolvedGrill.handGrillage,
  ]
    .filter(Boolean)
    .join("  ");
  const refundTag = item.refundType ? "【退洗】" : "";
  const productName = truncatePrintProductName(
    item.productName,
    tpl,
    PRINT_SCENES.STORE_WASH,
  );
  const product = [
    productName,
    item.totalQuantity ? `共${item.totalQuantity}件` : "",
  ]
    .filter(Boolean)
    .join("  ");
  let flaw = Array.isArray(item.productFlaw)
    ? item.productFlaw.join()
    : item.productFlaw;
  if (!flaw) {
    flaw = "见图片";
  } else {
    flaw = truncateText(flaw, tpl.truncate.flaw, flaw);
  }
  const annexArr = item.productAnnex;
  const annexCount = annexArr
    ? (Array.isArray(annexArr) ? annexArr.length : String(annexArr).split(",").length)
    : 0;
  return {
    title,
    customer,
    phone: item.senderPhone,
    channel,
    gird,
    refundTag,
    product,
    flaw,
    annex: annexCount ? String(annexCount) : "无",
  };
}

export function renderOnlineTopicNative(LODOP, tpl, item, printName) {
  buildOnlineTopicNative(LODOP, tpl, item, printName);
}

export function renderStoreTopicNative(LODOP, tpl, item, printName, grillObj, isText) {
  buildStoreTopicNative(LODOP, tpl, item, printName, grillObj, isText);
}

export function renderWashNative(LODOP, tpl, data, options) {
  buildWashNative(LODOP, tpl, data, options);
}

export function renderAnnexNative(LODOP, tpl, data, options) {
  buildWashNative(LODOP, tpl, data, { ...options, isAnnex: true });
}

export { PRINT_SCENES };
